/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { TaxInformation } from './TaxInformation';
import {
    TaxInformationFromJSON,
    TaxInformationFromJSONTyped,
    TaxInformationToJSON,
} from './TaxInformation';
import type { InvestorCompliance } from './InvestorCompliance';
import {
    InvestorComplianceFromJSON,
    InvestorComplianceFromJSONTyped,
    InvestorComplianceToJSON,
} from './InvestorCompliance';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { ProductPermissionEnum } from './ProductPermissionEnum';
import {
    ProductPermissionEnumFromJSON,
    ProductPermissionEnumFromJSONTyped,
    ProductPermissionEnumToJSON,
} from './ProductPermissionEnum';
import type { KycStatusEnum } from './KycStatusEnum';
import {
    KycStatusEnumFromJSON,
    KycStatusEnumFromJSONTyped,
    KycStatusEnumToJSON,
} from './KycStatusEnum';
import type { Wallet } from './Wallet';
import {
    WalletFromJSON,
    WalletFromJSONTyped,
    WalletToJSON,
} from './Wallet';
import type { DistributionPlatform } from './DistributionPlatform';
import {
    DistributionPlatformFromJSON,
    DistributionPlatformFromJSONTyped,
    DistributionPlatformToJSON,
} from './DistributionPlatform';
import type { ManualKycEnum } from './ManualKycEnum';
import {
    ManualKycEnumFromJSON,
    ManualKycEnumFromJSONTyped,
    ManualKycEnumToJSON,
} from './ManualKycEnum';
import type { InvestorEntryType } from './InvestorEntryType';
import {
    InvestorEntryTypeFromJSON,
    InvestorEntryTypeFromJSONTyped,
    InvestorEntryTypeToJSON,
} from './InvestorEntryType';
import type { Person } from './Person';
import {
    PersonFromJSON,
    PersonFromJSONTyped,
    PersonToJSON,
} from './Person';

/**
 * 
 * @export
 * @interface AdminInvestor
 */
export interface AdminInvestor {
    /**
     * 
     * @type {string}
     * @memberof AdminInvestor
     */
    readonly id: string;
    /**
     * 
     * @type {Person}
     * @memberof AdminInvestor
     */
    person: Person;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof AdminInvestor
     */
    surveyAnswers: { [key: string]: any; };
    /**
     * time of survey submission
     * @type {Date}
     * @memberof AdminInvestor
     */
    readonly surveySubmittedAt: Date | null;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestor
     */
    readonly voucherCode: string | null;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestor
     */
    readonly investorName: string;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestor
     */
    readonly email: string | null;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestor
     */
    readonly distributionPlatformId: string;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestor
     */
    readonly isSubjectToUsTax: boolean;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestor
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {number}
     * @memberof AdminInvestor
     */
    readonly activeInvestmentsNo: number | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestor
     */
    readonly activeInvestmentsSum: Money;
    /**
     * Investor can re-identify, if the don't have a valid identification
     * @type {boolean}
     * @memberof AdminInvestor
     */
    readonly canReidentify: boolean;
    /**
     * 
     * @type {InvestorCompliance}
     * @memberof AdminInvestor
     */
    readonly compliance: InvestorCompliance | null;
    /**
     * 
     * @type {DistributionPlatform}
     * @memberof AdminInvestor
     */
    readonly distributionPlatform: DistributionPlatform;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestor
     */
    readonly earliestDeletion: Date;
    /**
     * 
     * @type {InvestorEntryType}
     * @memberof AdminInvestor
     */
    readonly entryType: InvestorEntryType;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestor
     */
    readonly hasUser: boolean;
    /**
     * 
     * @type {KycStatusEnum}
     * @memberof AdminInvestor
     */
    readonly kycStatus: KycStatusEnum;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestor
     */
    readonly manualKycAt: Date | null;
    /**
     * KnowYourCustomer. combines manual KYC check (natural) and business KYC (legal).
     * @type {ManualKycEnum}
     * @memberof AdminInvestor
     */
    readonly manualKyc: ManualKycEnum;
    /**
     * 
     * @type {Array<ProductPermissionEnum>}
     * @memberof AdminInvestor
     */
    readonly permissions: Array<ProductPermissionEnum>;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestor
     */
    readonly target2Bic: string | null;
    /**
     * 
     * @type {TaxInformation}
     * @memberof AdminInvestor
     */
    readonly taxInformation: TaxInformation;
    /**
     * 
     * @type {Array<Wallet>}
     * @memberof AdminInvestor
     */
    readonly wallets: Array<Wallet>;
}



/**
 * Check if a given object implements the AdminInvestor interface.
 */
export function instanceOfAdminInvestor(value: object): value is AdminInvestor {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('person' in value) || value['person'] === undefined) return false;
    if (!('surveyAnswers' in value) || value['surveyAnswers'] === undefined) return false;
    if (!('surveySubmittedAt' in value) || value['surveySubmittedAt'] === undefined) return false;
    if (!('voucherCode' in value) || value['voucherCode'] === undefined) return false;
    if (!('investorName' in value) || value['investorName'] === undefined) return false;
    if (!('email' in value) || value['email'] === undefined) return false;
    if (!('distributionPlatformId' in value) || value['distributionPlatformId'] === undefined) return false;
    if (!('isSubjectToUsTax' in value) || value['isSubjectToUsTax'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('activeInvestmentsNo' in value) || value['activeInvestmentsNo'] === undefined) return false;
    if (!('activeInvestmentsSum' in value) || value['activeInvestmentsSum'] === undefined) return false;
    if (!('canReidentify' in value) || value['canReidentify'] === undefined) return false;
    if (!('compliance' in value) || value['compliance'] === undefined) return false;
    if (!('distributionPlatform' in value) || value['distributionPlatform'] === undefined) return false;
    if (!('earliestDeletion' in value) || value['earliestDeletion'] === undefined) return false;
    if (!('entryType' in value) || value['entryType'] === undefined) return false;
    if (!('hasUser' in value) || value['hasUser'] === undefined) return false;
    if (!('kycStatus' in value) || value['kycStatus'] === undefined) return false;
    if (!('manualKycAt' in value) || value['manualKycAt'] === undefined) return false;
    if (!('manualKyc' in value) || value['manualKyc'] === undefined) return false;
    if (!('permissions' in value) || value['permissions'] === undefined) return false;
    if (!('target2Bic' in value) || value['target2Bic'] === undefined) return false;
    if (!('taxInformation' in value) || value['taxInformation'] === undefined) return false;
    if (!('wallets' in value) || value['wallets'] === undefined) return false;
    return true;
}

export function AdminInvestorFromJSON(json: any): AdminInvestor {
    return AdminInvestorFromJSONTyped(json, false);
}

export function AdminInvestorFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminInvestor {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'person': PersonFromJSON(json['person']),
        'surveyAnswers': json['survey_answers'],
        'surveySubmittedAt': (json['survey_submitted_at'] == null ? null : new Date(json['survey_submitted_at'])),
        'voucherCode': json['voucher_code'],
        'investorName': json['investor_name'],
        'email': json['email'],
        'distributionPlatformId': json['distribution_platform_id'],
        'isSubjectToUsTax': json['is_subject_to_us_tax'],
        'createdAt': (new Date(json['created_at'])),
        'activeInvestmentsNo': json['active_investments_no'],
        'activeInvestmentsSum': MoneyFromJSON(json['active_investments_sum']),
        'canReidentify': json['can_reidentify'],
        'compliance': InvestorComplianceFromJSON(json['compliance']),
        'distributionPlatform': DistributionPlatformFromJSON(json['distribution_platform']),
        'earliestDeletion': (new Date(json['earliest_deletion'])),
        'entryType': InvestorEntryTypeFromJSON(json['entry_type']),
        'hasUser': json['has_user'],
        'kycStatus': KycStatusEnumFromJSON(json['kyc_status']),
        'manualKycAt': (json['manual_kyc_at'] == null ? null : new Date(json['manual_kyc_at'])),
        'manualKyc': ManualKycEnumFromJSON(json['manual_kyc']),
        'permissions': ((json['permissions'] as Array<any>).map(ProductPermissionEnumFromJSON)),
        'target2Bic': json['target2_bic'],
        'taxInformation': TaxInformationFromJSON(json['tax_information']),
        'wallets': ((json['wallets'] as Array<any>).map(WalletFromJSON)),
    };
}

export function AdminInvestorToJSON(value?: Omit<AdminInvestor, 'id'|'survey_submitted_at'|'voucher_code'|'investor_name'|'email'|'distribution_platform_id'|'is_subject_to_us_tax'|'created_at'|'active_investments_no'|'active_investments_sum'|'can_reidentify'|'compliance'|'distribution_platform'|'earliest_deletion'|'entry_type'|'has_user'|'kyc_status'|'manual_kyc_at'|'manual_kyc'|'permissions'|'target2_bic'|'tax_information'|'wallets'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'person': PersonToJSON(value['person']),
        'survey_answers': value['surveyAnswers'],
    };
}

